import { useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function About() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  return (
    <>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '天天漫画网',
          url: 'https://www.tiantianmanhua.com',
          description: '专注于韩国漫画的在线免费阅读平台，提供下拉式完整版无删减漫画阅读体验',
          email: 'contact@tiantianmanhua.com',
          sameAs: ['https://www.tiantianmanhua.com'],
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: '天天漫画网是免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '是的！天天漫画网上的所有漫画内容均完全免费阅读，无需任何付费订阅或购买会员。' } },
            { '@type': 'Question', name: '漫画是否有删减？', acceptedAnswer: { '@type': 'Answer', text: '我们对所有漫画都坚持提供完整版内容，绝无任何删减或修改。' } },
            { '@type': 'Question', name: '如何阅读漫画？', acceptedAnswer: { '@type': 'Answer', text: '您可以直接在网页中下拉阅读，也可以在手机浏览器中打开阅读。我们采用下拉式Webtoon阅读模式。' } },
            { '@type': 'Question', name: '更新速度如何？', acceptedAnswer: { '@type': 'Answer', text: '天天漫画网与各大韩国漫画平台保持同步更新，热门连载漫画通常每日或隔日更新最新章节。' } },
            { '@type': 'Question', name: '如何反馈问题？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过电子邮件、微信公众号或在线客服与我们联系。' } },
          ],
        }}
      />
      <Header />
      <main className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3">
            <nav className="flex items-center gap-2 text-xs text-foreground-600">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-950 font-medium">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-6">
              关于天天漫画网
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              天天漫画网主打下拉式完整版免费阅读，是一个专注于韩国漫画（Webtoon）的在线免费阅读平台。
              自创立以来，我们始终坚持为用户提供无删减、高质量、流畅便捷的长条漫阅读体验，
              让每一位漫友都能在这里找到自己喜爱的作品，每天解锁完整版韩国漫画的新鲜剧情。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="bg-background-100 py-16 md:py-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
                品牌故事
              </h2>
              <div className="space-y-6 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  天天漫画网诞生于一群韩国漫画爱好者的热情与坚持。作为资深的漫画迷，
                  我们的创始团队深感国内漫画阅读体验的不足——大量删减、翻译质量参差不齐、
                  阅读体验不够流畅。于是，我们决定自己打造一个真正为漫友着想的漫画阅读平台。
                </p>
                <p>
                  我们与多家韩国漫画平台建立了深度合作关系，确保每部漫画都能以最快的速度、
                  最完整的版本呈现在读者面前。从选品到翻译，从排版到上线，每一个环节
                  我们都严格把控，只为给漫友们带来最优质的阅读体验。
                </p>
                <p>
                  经过多年的发展，天天漫画网已经成长为中国领先的韩国漫画阅读平台之一，
                  拥有超过2000部优质韩国漫画资源，涵盖恋爱、奇幻、动作、悬疑、搞笑、日常、
                  剧情、热血等八大分类，每日更新最新章节，累计服务超过百万漫友。
                </p>
                <p>
                  我们相信，每一部好的漫画都值得被完整地阅读。天天漫画网将继续秉持
                  "无删减、免费读、体验好"的理念，为更多漫友提供优质的漫画阅读服务。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { num: '2000+', label: '漫画资源' },
              { num: '100万+', label: '忠实漫友' },
              { num: '每日更新', label: '稳定连载' },
              { num: '99.7%', label: '用户好评率' },
            ].map((stat) => (
              <div key={stat.label} className="text-center p-6 rounded-2xl bg-background-100">
                <p className="text-2xl md:text-3xl font-bold text-primary-500">{stat.num}</p>
                <p className="text-sm text-foreground-600 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* User Reviews */}
        <section className="bg-background-100 py-16 md:py-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              漫友口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                {
                  name: '小雨同学',
                  avatar: 'ri-user-3-line',
                  content: '真的超级好用！漫画更新速度很快，而且完全没有删减，画质也很清晰。下拉式阅读体验太棒了，特别适合在手机上追番。已经推荐给身边所有喜欢韩漫的朋友了！',
                  rating: 5,
                },
                {
                  name: '追漫少年',
                  avatar: 'ri-user-3-line',
                  content: '资源真的很多，各种类型的韩漫都有。最让我感动的是全部免费，而且没有广告干扰。翻译质量也很高，能看出团队是非常用心在做这个平台。',
                  rating: 5,
                },
                {
                  name: '柠檬不酸',
                  avatar: 'ri-user-3-line',
                  content: '从天天漫画网刚上线就开始用了，看着平台一点点成长起来。现在已经是我每天必刷的网站了，等更新的日子虽然煎熬但很快乐哈哈哈。希望平台越做越好！',
                  rating: 5,
                },
              ].map((review) => (
                <div key={review.name} className="bg-background-50 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className={`${review.avatar} text-primary-500`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{review.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <span key={i} className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px] text-accent-500"></i>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20" id="contact">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
            联系我们
          </h2>
          <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-8 rounded-2xl bg-background-100">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-500 text-background-50 mb-4">
                <i className="ri-mail-line text-2xl"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">电子邮件</h3>
              <p className="text-xs text-foreground-600">contact@tiantianmanhua.com</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-background-100">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-500 text-background-50 mb-4">
                <i className="ri-wechat-line text-2xl"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">微信公众号</h3>
              <p className="text-xs text-foreground-600">天天漫画网</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-background-100">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-500 text-background-50 mb-4">
                <i className="ri-customer-service-2-line text-2xl"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">客服支持</h3>
              <p className="text-xs text-foreground-600">工作日 9:00 - 18:00</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background-100 py-16 md:py-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              常见问题解答
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {[
                { q: '天天漫画网是免费的吗？', a: '是的！天天漫画网上的所有漫画内容均完全免费阅读，无需任何付费订阅或购买会员。我们致力于为广大漫友提供最优质的免费漫画阅读体验。' },
                { q: '漫画是否有删减？', a: '我们对所有漫画都坚持提供完整版内容，绝无任何删减或修改。您可以放心享受原汁原味的韩国漫画内容，每一页都不放过。' },
                { q: '如何阅读漫画？', a: '您可以直接在网页中下拉阅读，也可以在手机浏览器中打开阅读。我们采用下拉式Webtoon阅读模式，只需向下滑动即可连续阅读，非常流畅便捷。' },
                { q: '更新速度如何？', a: '天天漫画网与各大韩国漫画平台保持同步更新，热门连载漫画通常每日或隔日更新最新章节。您可以收藏喜欢的漫画，及时获取更新通知。' },
                { q: '如何反馈问题？', a: '您可以通过电子邮件、微信公众号或在线客服与我们联系。我们会在工作时间内尽快回复您的问题和建议。' },
              ].map((faq, idx) => (
                <details key={idx} className="bg-background-50 rounded-xl p-5 group cursor-pointer">
                  <summary className="text-sm font-semibold text-foreground-950 list-none flex items-center justify-between">
                    {faq.q}
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </span>
                  </summary>
                  <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}